import React, { useState, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';

/**
 * A lightweight, searchable dropdown component that matches the project's design.
 * @param {Array} options - [{ value, label, sublabel }]
 * @param {string} value - Current selected value
 * @param {function} onChange - Callback for value change
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Optional CSS classes
 * @param {object} style - Optional inline styles
 * @param {function} onAddCustom - Optional callback when adding custom options
 */
const SearchableSelect = ({ 
  options = [], 
  value = '', 
  onChange, 
  placeholder = 'Select...', 
  className = '', 
  style = {},
  onAddCustom
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  // Find the label of the currently selected value
  const selectedOption = options.find(opt => String(opt.value) === String(value));
  const displayValue = selectedOption ? selectedOption.label : '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (opt.sublabel && opt.sublabel.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const inputRef = useRef(null);

  // Reset focused item index when options or search term change
  useEffect(() => {
    // Default focus to 0 if there are filtered options, so typing lets them instantly choose the first match
    setFocusedIndex(filteredOptions.length > 0 ? 0 : -1);
  }, [searchTerm, isOpen, filteredOptions.length]);

  // Scroll active/focused element into view automatically
  useEffect(() => {
    if (focusedIndex >= 0 && dropdownRef.current) {
      // Offset by 1 if Add Custom option is pinned to the top of the children list
      const offset = onAddCustom && searchTerm ? 1 : 0;
      const activeEl = dropdownRef.current.children[focusedIndex + offset];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex, onAddCustom, searchTerm]);

  const handleSelect = (opt) => {
    onChange({ target: { value: opt.value } });
    setIsOpen(false);
    setSearchTerm('');
    setFocusedIndex(-1);
    // Blur the input element to finish editing
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1 < filteredOptions.length ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev - 1 >= 0 ? prev - 1 : filteredOptions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[focusedIndex]);
        } else if (onAddCustom && searchTerm) {
          onAddCustom(searchTerm);
          setIsOpen(false);
          setSearchTerm('');
          setFocusedIndex(-1);
          if (inputRef.current) {
            inputRef.current.blur();
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
        if (inputRef.current) {
          inputRef.current.blur();
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={`searchable-select-wrapper ${className}`} ref={wrapperRef} style={{ position: 'relative', ...style }}>
      <div className="position-relative">
        <Form.Control
            ref={inputRef}
            type="text"
            size="sm"
            placeholder={placeholder}
            value={isOpen ? searchTerm : displayValue}
            onChange={(e) => {
                setIsOpen(true);
                setSearchTerm(e.target.value);
            }}
            onClick={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            className="border-0 bg-light pr-4"
            style={{ fontSize: '0.75rem', cursor: 'pointer' }}
            autoComplete="off"
        />
        {value ? (
          <i 
              className="fas fa-times-circle position-absolute text-muted hover-text-dark" 
              style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.75rem', cursor: 'pointer', zIndex: 5 }}
              onClick={(e) => {
                e.stopPropagation();
                onChange({ target: { value: '' } });
                setSearchTerm('');
                setIsOpen(false);
                setFocusedIndex(-1);
              }}
              title="Clear selection"
          ></i>
        ) : (
          <i 
              className={`fas fa-search position-absolute ${isOpen ? 'text-primary' : 'text-muted'}`} 
              style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.65rem', pointerEvents: 'none' }}
          ></i>
        )}
      </div>
      
      {isOpen && (
        <div ref={dropdownRef} className="shadow-lg border rounded" style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 2000,
          backgroundColor: 'white',
          maxHeight: '250px',
          overflowY: 'auto',
          marginTop: '2px'
        }}>
          {onAddCustom && searchTerm && (
            <div 
              className="p-2 text-primary font-weight-bold text-center border-bottom bg-light"
              style={{ cursor: 'pointer', fontSize: '0.75rem', position: 'sticky', top: 0, zIndex: 10 }}
              onClick={() => {
                onAddCustom(searchTerm);
                setIsOpen(false);
                setSearchTerm('');
                setFocusedIndex(-1);
              }}
            >
              <i className="fas fa-plus mr-1"></i> Add new "{searchTerm}"
            </div>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, idx) => {
              const isSelected = String(opt.value) === String(value);
              const isFocused = idx === focusedIndex;
              return (
                <div 
                  key={idx}
                  className={`p-2 border-bottom ${isSelected ? 'bg-primary text-white' : ''}`}
                  style={{ 
                    cursor: 'pointer', 
                    transition: 'background 0.2s',
                    backgroundColor: isSelected ? '' : (isFocused ? '#deebff' : 'white'),
                    color: isSelected ? 'white' : (isFocused ? '#0747a6' : 'black')
                  }}
                  onClick={() => handleSelect(opt)}
                  onMouseEnter={() => setFocusedIndex(idx)}
                >
                  <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{opt.label}</div>
                  {opt.sublabel && <div className={isSelected ? 'text-light' : 'text-muted'} style={{ fontSize: '0.65rem' }}>{opt.sublabel}</div>}
                </div>
              );
            })
          ) : (
            <div className="p-3 text-center text-muted small">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
